<template>
  <div class="plaza-container">
    <!-- Featured Section (hidden) -->
    <section v-if="false" class="featured-section">
      <div class="section-inner">
        <div class="section-header">
          <h2 class="section-title">
            <i class="el-icon-star-on featured-icon"></i>
            Featured
          </h2>
          <span class="section-subtitle">Handpicked for you</span>
        </div>
        <div class="featured-grid" v-loading="featuredLoading">
          <div
            v-for="item in featuredItems"
            :key="getItemId(item)"
            class="featured-card"
            @click="openCollection(item)"
          >
            <div class="featured-cover">
              <CachedImage
                v-if="item.cover_url"
                :src="item.cover_url"
                :fallback-src="placeholderImage"
                fit="cover"
                width="100%"
                height="100%"
                lazy
              />
              <div v-else class="cover-placeholder">
                <i class="el-icon-reading"></i>
              </div>
              <div class="featured-overlay">
                <span class="featured-badge">Featured</span>
              </div>
            </div>
            <div class="featured-info">
              <h3 class="featured-title">{{ item.title }}</h3>
              <p class="featured-desc">{{ item.description || 'Explore this course' }}</p>
              <div class="featured-meta">
                <el-avatar :size="22" :src="item.user_avatar" class="meta-avatar">
                  {{ item.user_name ? item.user_name.charAt(0) : '?' }}
                </el-avatar>
                <span class="meta-author">{{ item.user_name || 'Unknown' }}</span>
              </div>
            </div>
          </div>
          <div v-if="!featuredLoading && featuredItems.length === 0" class="empty-featured">
            <i class="el-icon-collection"></i>
            <p>No featured content yet. Stay tuned!</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Filter Bar -->
    <section class="filter-section">
      <div class="section-inner">
        <div class="filter-bar">
          <div class="tag-filters">
            <el-button
              :type="activeChannel === '' ? 'primary' : 'default'"
              size="small"
              round
              @click="filterByChannel('')"
            >All</el-button>
            <el-button
              v-for="channel in channels"
              :key="channel.user_id"
              :type="activeChannel === channel.user_id ? 'primary' : 'default'"
              size="small"
              round
              @click="filterByChannel(channel.user_id)"
            >
              {{ channel.name }}
            </el-button>
          </div>
          <div class="sort-control">
            <el-select v-model="sortBy" size="small" @change="handleSortChange" style="width: 140px">
              <el-option label="Latest" value="latest"></el-option>
              <el-option label="Popular" value="popular"></el-option>
            </el-select>
          </div>
        </div>
      </div>
    </section>

    <!-- Content Grid -->
    <section class="content-section">
      <div class="section-inner">
        <div class="content-grid" v-loading="contentLoading && page === 1">
          <div
            v-for="item in contentItems"
            :key="getItemId(item)"
            class="content-card"
            @click="openCollection(item)"
          >
            <div class="card-cover">
              <CachedImage
                v-if="item.cover_url"
                :src="item.cover_url"
                :fallback-src="placeholderImage"
                fit="cover"
                width="100%"
                height="100%"
                lazy
              />
              <div v-else class="cover-placeholder">
                <i class="el-icon-reading"></i>
              </div>
            </div>
            <div class="card-body">
              <h4 class="card-title">{{ item.title }}</h4>
              <p class="card-desc">{{ item.description || 'No description' }}</p>
              <div class="card-footer">
                <div class="card-author">
                  <el-avatar :size="20" :src="item.user_avatar" class="author-avatar">
                    {{ item.user_name ? item.user_name.charAt(0) : '?' }}
                  </el-avatar>
                  <span>{{ item.user_name || 'Unknown' }}</span>
                </div>
                <div class="card-tags" v-if="item.tags && item.tags.length > 0">
                  <el-tag
                    v-for="tag in (item.tags || []).slice(0, 2)"
                    :key="tag"
                    size="mini"
                    effect="plain"
                    class="card-tag"
                  >{{ tag }}</el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!contentLoading && contentItems.length === 0" class="empty-content">
          <i class="el-icon-folder-opened" style="font-size: 48px; color: #c0c4cc;"></i>
          <p>No content found{{ activeTag ? ' for "' + activeTag + '"' : '' }}.</p>
          <el-button v-if="activeTag" type="text" @click="filterByTag('')">Clear filter</el-button>
        </div>

        <!-- Load More -->
        <div class="load-more" v-if="hasMore && contentItems.length > 0">
          <el-button
            :loading="contentLoading"
            type="text"
            @click="loadMore"
          >
            {{ contentLoading ? 'Loading...' : 'Load More' }}
          </el-button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CachedImage from '@/components/CachedImage.vue'
import { postRequest } from '@/apis/front'

export default {
  name: 'Plaza',
  components: {
    CachedImage
  },
  data() {
    return {
      placeholderImage: require('@/assets/Card Collection Cover Placeholder.jpeg'),
      featuredItems: [],
      featuredLoading: true,
      contentItems: [],
      contentLoading: false,
      channels: [
        { name: 'World Economics Cup', user_id: '68b11ba594c784d9568b4596' },
        { name: 'IPsyO', user_id: '68b11bcc94c784da568b458f' }
      ],
      activeChannel: '',
      sortBy: 'latest',
      page: 1,
      pageSize: 20,
      hasMore: false
    }
  },
  mounted() {
    // this.fetchFeatured()
    this.fetchContent()
  },
  methods: {
    getItemId(item) {
      if (item._id) {
        return item._id.$oid || item._id.$id || item._id
      }
      return item.id || Math.random().toString(36)
    },
    async fetchFeatured() {
      this.featuredLoading = true
      try {
        const response = await postRequest('getPlazaFeatured', {})
        if (response.data.code === 0) {
          this.featuredItems = response.data.data || []
        }
      } catch (error) {
        console.error('Failed to fetch featured content:', error)
      } finally {
        this.featuredLoading = false
      }
    },
    async fetchContent(append = false) {
      this.contentLoading = true
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize,
          sort_by: this.sortBy
        }
        if (this.activeChannel) {
          params.channel_user_id = this.activeChannel
        }
        const response = await postRequest('getPlazaContent', params)
        if (response.data.code === 0) {
          const data = response.data.data
          if (append) {
            this.contentItems = [...this.contentItems, ...(data.items || [])]
          } else {
            this.contentItems = data.items || []
          }
          this.hasMore = data.has_more || false
        }
      } catch (error) {
        console.error('Failed to fetch plaza content:', error)
      } finally {
        this.contentLoading = false
      }
    },
    filterByChannel(channelUserId) {
      this.activeChannel = channelUserId
      this.page = 1
      this.fetchContent()
    },
    handleSortChange() {
      this.page = 1
      this.fetchContent()
    },
    loadMore() {
      this.page++
      this.fetchContent(true)
    },
    openCollection(item) {
      const id = this.getItemId(item)
      this.$store.dispatch('setTempCollectionId', id)
      this.$router.push({ path: '/deck', query: { id, source: 'plaza' } })
    }
  }
}
</script>

<style scoped>
.plaza-container {
  width: 100%;
  min-height: 100vh;
  background: #f7f8fc;
}

/* ── Section Layout ── */
.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Featured Section ── */
.featured-section {
  padding: 32px 0 24px;
  background: linear-gradient(180deg, #fff 0%, #f7f8fc 100%);
}

.section-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
}

.section-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.3px;
}

.featured-icon {
  color: #e6a23c;
  margin-right: 4px;
}

.section-subtitle {
  font-size: 14px;
  color: #909399;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  min-height: 180px;
}

.featured-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.featured-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.featured-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #eef1f6;
  overflow: hidden;
}

.featured-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
}

.featured-badge {
  background: rgba(230, 162, 60, 0.9);
  color: #fff;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.featured-info {
  padding: 14px 16px 16px;
}

.featured-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-desc {
  margin: 0 0 10px;
  font-size: 13px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.featured-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-avatar {
  background: #e0e0e0;
  color: #606266;
  font-size: 11px;
}

.meta-author {
  font-size: 12px;
  color: #606266;
}

.empty-featured {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-featured i {
  font-size: 36px;
  color: #c0c4cc;
  margin-bottom: 8px;
}

/* ── Filter Section ── */
.filter-section {
  padding: 16px 0;
  background: #fff;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  position: sticky;
  top: 60px;
  z-index: 100;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.tag-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  align-items: center;
}

.tag-count {
  opacity: 0.65;
  font-size: 11px;
  margin-left: 2px;
}

.sort-control {
  flex-shrink: 0;
}

/* ── Content Section ── */
.content-section {
  padding: 24px 0 60px;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  min-height: 200px;
}

.content-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.content-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.card-cover {
  width: 100%;
  aspect-ratio: 3 / 2;
  background: #eef1f6;
  overflow: hidden;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.cover-placeholder i {
  font-size: 32px;
  color: #b0bac5;
}

.card-body {
  padding: 12px 14px 14px;
}

.card-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
}

.card-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.card-author {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}

.author-avatar {
  background: #e0e0e0;
  color: #606266;
  font-size: 10px;
  flex-shrink: 0;
}

.card-author span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.card-tag {
  font-size: 11px;
  border-color: #e4e7ed;
  color: #909399;
}

.empty-content {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-content p {
  margin: 12px 0;
}

.load-more {
  text-align: center;
  padding: 24px 0;
}

/* ── Responsive ── */
@media (max-width: 1100px) {
  .featured-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .content-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .featured-grid {
    grid-template-columns: 1fr;
  }
  .content-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  .section-inner {
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
